import { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";

export function useOpenExamSessions(session) {
  const [openExamSessions, setOpenExamSessions] = useState([]);
  const [loadingOpenExams, setLoadingOpenExams] = useState(false);

  const fetchOpenExamSessions = async () => {
    if (!session?.user) return;

    setLoadingOpenExams(true);

    const { data: enrollments, error: enrollmentsError } = await supabase
      .from("cohort_enrollments")
      .select("cohort_id")
      .eq("user_id", session.user.id)
      .eq("enrollment_status", "active");

    if (enrollmentsError) {
      alert(enrollmentsError.message);
      setLoadingOpenExams(false);
      return;
    }

    const cohortIds = (enrollments || []).map((e) => e.cohort_id);

    if (cohortIds.length === 0) {
      setOpenExamSessions([]);
      setLoadingOpenExams(false);
      return;
    }

    const { data: exams, error: examsError } = await supabase
      .from("exam_sessions")
      .select(
        "id, exam_set_id, session_name, duration_minutes, session_status, start_time, end_time, review_available, created_at"
      )
      .eq("session_status", "open")
      .in("cohort_id", cohortIds)
      .order("created_at", { ascending: false });

    if (examsError) {
      console.error("FETCH OPEN EXAMS ERROR:", examsError);
      alert(examsError.message);
      setLoadingOpenExams(false);
      return;
    }

    const examSessionIds = (exams || []).map((exam) => exam.id);

    if (examSessionIds.length === 0) {
      setOpenExamSessions([]);
      setLoadingOpenExams(false);
      return;
    }

    const { data: attempts, error: attemptsError } = await supabase
      .from("exam_attempts")
      .select(
        "id, exam_session_id, attempt_status, score, total_questions, accuracy_pct, submitted_at"
      )
      .eq("user_id", session.user.id)
      .in("exam_session_id", examSessionIds)
      .order("submitted_at", { ascending: false });

    if (attemptsError) {
      console.error("FETCH ATTEMPTS ERROR:", attemptsError);
      alert(attemptsError.message);
      setLoadingOpenExams(false);
      return;
    }

    const { data: retakePermissions, error: retakeError } = await supabase
      .from("exam_retake_permissions")
      .select("id, exam_session_id, reason, status, used_at, created_at")
      .eq("user_id", session.user.id)
      .eq("status", "active")
      .is("used_at", null)
      .in("exam_session_id", examSessionIds);

    if (retakeError) {
      console.error("FETCH RETAKE PERMISSIONS ERROR:", retakeError);
      alert(retakeError.message);
      setLoadingOpenExams(false);
      return;
    }

    const examsWithAttempts = (exams || []).map((exam) => {
      const submittedAttempt = (attempts || []).find(
        (attempt) =>
          attempt.exam_session_id === exam.id &&
          attempt.attempt_status === "submitted"
      );

      const activeRetakePermission = (retakePermissions || []).find(
        (permission) => permission.exam_session_id === exam.id
      );

      return {
        ...exam,
        submittedAttempt: submittedAttempt || null,
        activeRetakePermission: activeRetakePermission || null,
      };
    });

    setOpenExamSessions(examsWithAttempts);
    setLoadingOpenExams(false);
  };

  useEffect(() => {
    fetchOpenExamSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id]);

  return {
    openExamSessions,
    loadingOpenExams,
    fetchOpenExamSessions,
  };
}